import { SelectWithRadio } from "@/components/form";
import AppLayout from "@/components/layout/AppLayout";
import NoDataContainer from "@/components/NoDataContainer";
import ProjectSummary from "@/components/ProjectSummary";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getProjectMonthlyBreakdownService } from "@/service/project.service";
import { getProjectDetail } from "@/store/slices/projectSlice";
import {
  fetchProjectOptions,
  selectProjectOptions,
} from "@/store/slices/timeEntrySlice";
import {
  selectSelectedOrganization,
  selectClientOrganizationLoading,
} from "@/store/slices/clientOrganizationSlice";
import { ProjectList, ProjectMonthlyAnalytics } from "@/types/project.model";
import { capitalizeFirstLetter } from "@/utils/constants";
import React, { useEffect, useRef, useState } from "react";

const ClientProject: React.FC = () => {
  const dispatch = useAppDispatch();

  const projectOptions = useAppSelector(selectProjectOptions);
  const selectedOrganization = useAppSelector(selectSelectedOrganization);
  const organizationLoading = useAppSelector(selectClientOrganizationLoading);

  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectList | null>(
    null
  );
  const [monthlyBreakdown, setMonthlyBreakdown] =
    useState<ProjectMonthlyAnalytics | null>(null);

  // Use refs to track if we've already made API calls to prevent duplicate calls
  const hasCalledMonthlyBreakdown = useRef(false);
  const hasCalledProjectOptions = useRef(false);

  // Fetch project options only after organization is loaded
  // Note: OrganizationDropdown component handles fetching organizations
  useEffect(() => {
    // if (hasCalledProjectOptions.current) return;

    // Wait for organization to be loaded and selected
    if (selectedOrganization) {
      hasCalledProjectOptions.current = true;
      dispatch(fetchProjectOptions({ search: "", clientId: selectedOrganization.client_id }));
      setSelectedProject(null)
    }
  }, [dispatch, organizationLoading, selectedOrganization]);

  // Handle project selection change
  const handleProjectChange = (projectId: string) => {
    // Fetch the latest project details from the API instead of using table data
    dispatch(getProjectDetail(projectId))
      .unwrap()
      .then((projectData: ProjectList) => {
        setSelectedProject(projectData);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch project details:", error);
      });
  };

  useEffect(() => {
    // Separate effect for monthly breakdown data to avoid dependency issues
    if (selectedProject?.id && !hasCalledMonthlyBreakdown.current) {
      // Set the ref to true to prevent duplicate calls
      hasCalledMonthlyBreakdown.current = true;

      setLoading(true);

      getProjectMonthlyBreakdownService(selectedProject?.id)
        .then((response) => {
          setMonthlyBreakdown(response.data.data);
        })
        .catch((err) => {
          console.error("Error fetching monthly breakdown:", err);
          // errorToast("Error", "Failed to load monthly breakdown data");
          // Reset the ref on error so we can try again if needed
          hasCalledMonthlyBreakdown.current = false;
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedProject?.id]);

  return (
    <AppLayout title="Projects">
      <div
        className="p-6 bg-[#fff] rounded-lg  min-h-[800px]"
        style={{
          boxShadow: "0px 10px 60px 0px #E2ECF980",
        }}
      >
        {/* Project selector */}
        <div className="mb-3">
          <SelectWithRadio
            label="Select project"
            required
            showAbove={false}
            options={projectOptions.map((project) => ({
              label: project.name,
              value: project.id,
            }))}
            value={selectedProject?.id || ""}
            onChange={(value) => handleProjectChange(value)}
          />
        </div>

        {/* Project summary section */}
        {selectedProject ? (
          <>
            <ProjectSummary
              projectName={capitalizeFirstLetter(selectedProject.name) || ""}
              projectId={selectedProject.id}
              startDate={selectedProject.start_date}
              endDate={selectedProject.end_date}
              hoursEstimated={
                (
                  selectedProject.tracker?.total_esti_hrs as number
                )?.toString() || "0"
              }
              hoursUtilized={
                (selectedProject.util_hrs as number)?.toString() || "0"
              }
              status={capitalizeFirstLetter(selectedProject.status)}
              remainingHours={selectedProject.remi_hrs?.toString() || "0"}
            />

            {/* Monthly breakdown */}
            <div
              className="mt-5 mb-8 bg-white rounded-lg px-5 py-6"
              style={{
                boxShadow: "0px 10px 60px 0px #E2ECF980",
              }}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E0E0E0] border border-t-0 border-r-0 border-[#E0E0E0]">
                  <thead className="bg-[#F4F7F9]">
                    <tr>
                      <th className="px-4 py-3 text-left text-[14px] font-semibold text-[#1E1E1E]">
                        Month
                      </th>
                      <th className="px-4 py-3 text-left text-[14px] font-semibold text-[#1E1E1E]">
                        Hours
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E0E0E0]">
                    {!loading ? (
                      <>
                        {monthlyBreakdown?.monthly_analytics &&
                          monthlyBreakdown.monthly_analytics.length > 0 ? (
                          monthlyBreakdown.monthly_analytics.map(
                            (item, index) => (
                              <tr key={index}>
                                <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                                  {item.month}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                                  {item.utilized_hours}
                                </td>
                                {/* <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                          {item.utilized_hours}hrs
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[14px] text-[#1E1E1E] font-medium">
                          {item.remaining_hours}hrs
                        </td> */}
                              </tr>
                            )
                          )
                        ) : (
                          <tr>
                            <td
                              colSpan={2}
                              className="px-4 py-3 text-center text-[14px] text-[#1E1E1E]"
                            >
                              No monthly data available
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-4 py-3 text-center text-[14px] text-[#1E1E1E]"
                        >
                          Loading...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <NoDataContainer title={`Select a project.`} />
        )}
      </div>
    </AppLayout>
  );
};

export default ClientProject;
